import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Pencil, Loader2, CheckCircle2, Archive } from 'lucide-react';
import { rescuedAnimalsApi } from '../api';
import { useLookupsSnapshot, toOptionLabel } from '@/features/lookups/snapshot';
import { toast } from '@/hooks/use-toast';

function statusVariant(code?: string): 'default' | 'secondary' | 'outline' | 'destructive' {
  if (code === 'published') return 'default';
  if (code === 'claimed') return 'secondary';
  if (code === 'expired') return 'outline';
  if (code === 'archived') return 'outline';
  return 'outline';
}

export default function RescuedAnimalsListPage() {
  const queryClient = useQueryClient();
  const { data: snapshot } = useLookupsSnapshot();
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['rescued-animals'],
    queryFn: () => rescuedAnimalsApi.list(),
  });

  const statusById = new Map<number, any>((snapshot?.rescuedAnimalStatuses ?? []).map((s: any) => [s.id, s]));
  const speciesById = new Map<number, any>((snapshot?.species ?? []).map((s: any) => [s.id, s]));
  const conditionById = new Map<number, any>((snapshot?.rescuedAnimalConditions ?? []).map((c: any) => [c.id, c]));

  const records: any[] = Array.isArray(data) ? data : (data?.data ?? []);
  const filtered = statusFilter === 'all'
    ? records
    : records.filter((r) => statusById.get(r.statusId)?.code === statusFilter);

  const handleSetStatus = async (id: number, statusCode: 'claimed' | 'archived' | 'published') => {
    try {
      await rescuedAnimalsApi.setStatus(id, statusCode);
      queryClient.invalidateQueries({ queryKey: ['rescued-animals'] });
      toast({ title: 'Actualizado', description: 'El estado se actualizó correctamente.' });
    } catch (e: any) {
      toast({
        title: 'Error',
        description: e?.response?.data?.error?.message || 'No se pudo actualizar el estado.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Animales encontrados</h1>
            <p className="text-sm text-muted-foreground">
              Animales rescatados publicados temporalmente para reunificación con sus dueños.
            </p>
          </div>
          <Button asChild>
            <Link to="/rescued-animals/new">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo registro
            </Link>
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {(snapshot?.rescuedAnimalStatuses ?? []).map((s: any) => (
                <SelectItem key={s.id} value={s.code}>{toOptionLabel(s)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Card>
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">No hay registros.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ingreso</TableHead>
                  <TableHead>Especie</TableHead>
                  <TableHead>Condición</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => {
                  const status = statusById.get(r.statusId);
                  const species = speciesById.get(r.speciesId);
                  const condition = conditionById.get(r.conditionId);
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{String(r.intakeDate).slice(0, 10)}</TableCell>
                      <TableCell>{species ? toOptionLabel(species) : '—'}</TableCell>
                      <TableCell>{condition ? toOptionLabel(condition) : '—'}</TableCell>
                      <TableCell>{r.foundArea}</TableCell>
                      <TableCell>
                        <Badge variant={statusVariant(status?.code)}>{status ? toOptionLabel(status) : '—'}</Badge>
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        {status?.code === 'published' && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSetStatus(r.id, 'claimed')}
                              title="Marcar como reclamado"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSetStatus(r.id, 'archived')}
                              title="Archivar"
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" asChild>
                          <Link to={`/rescued-animals/${r.id}/edit`}>
                            <Pencil className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </Layout>
  );
}
